/**
 * SCRIPT PARSING RULES & HEURISTICS
 * 
 * 1. Metadata Detection: Identify front matter (Title, Author, etc.)
 * 2. Scene Headings: Detected via INT./EXT. prefixes.
 * 3. ShotCue Detection: Camera directions (CLOSE UP, MACRO, etc.) in EN, PT, ES, FR.
 *    RULE: ShotCue entries are NEVER classified as characters, even if in ALL CAPS.
 * 4. Character Validation: ALL CAPS lines are only characters if followed by dialogue 
 *    within 3 non-empty lines. If a scene-heading, transition, or shot-cue 
 *    appears first, it's NOT a character.
 * 5. SFX Detection: Identified via SFX: prefix or known onomatopoeia.
 */
import { ScriptBlock, BlockType, CameraMeta, Language, ScriptFormatType } from '@/types';
import { SHOT_CUE_DICTIONARY, normalizeText, stripTrailingPunctuation } from './shotCueDictionary';
import { runMechanicalLinter } from './mechanicalLinter';

/**
 * Translates default script version names between languages.
 * Detects patterns like "1ª Versão", "Draft 1", "1ª Versión", etc.
 * Preserves suffixes like "(Pillars)" or "(EN)".
 * Returns the original name if it doesn't match a default pattern.
 */
export const translateVersionName = (name: string, lang: string): string => {
  const keywords = ['Versão', 'Draft', 'Versión', 'Version', 'Roteiro', 'Script', 'Guion', 'Scénario', 'Rascunho'];
  const keywordPattern = keywords.join('|');

  // Pattern 1: "Numberª Keyword Suffix" (e.g., "1ª Versão (Pillars)")
  const newPattern = new RegExp(`^(\\d+)ª\\s+(${keywordPattern})(\\s*\\(.*\\))?$`, 'i');
  let match = name.match(newPattern);
  if (match) {
    const num = match[1];
    const suffix = match[3] || '';
    let baseName = 'Draft';
    if (lang === 'pt') baseName = 'Versão';
    if (lang === 'es') baseName = 'Versión';
    if (lang === 'fr') baseName = 'Version';
    return `${num}ª ${baseName}${suffix}`;
  }

  // Pattern 2: "Keyword Number Suffix" (e.g., "Draft 1")
  const oldPattern = new RegExp(`^(${keywordPattern})\\s*(\\d+)(\\s*\\(.*\\))?$`, 'i');
  match = name.match(oldPattern);
  if (match) {
    const num = match[2];
    const suffix = match[3] || '';
    let baseName = 'Draft';
    if (lang === 'pt') baseName = 'Versão';
    if (lang === 'es') baseName = 'Versión';
    if (lang === 'fr') baseName = 'Version';
    return `${num}ª ${baseName}${suffix}`;
  }

  return name;
};

export const serializeBlocksToFountain = (blocks: ScriptBlock[]): string => {
  return blocks.map(b => {
    let text = b.content;
    if (b.type === 'character') return text.toUpperCase();
    if (b.type === 'dialogue') return text; // Fountain doesn't strictly need dialogue indentation in raw text, but let's be safe
    if (b.type === 'parenthetical') {
      const trimmed = text.trim();
      return trimmed.startsWith('(') && trimmed.endsWith(')') ? trimmed : `(${trimmed})`;
    }
    if (b.type === 'scene-heading') return text.toUpperCase();
    if (b.type === 'transition') return text.toUpperCase();
    return text;
  }).join('\n\n');
};

export const getEstimatedBlockLineCount = (block: ScriptBlock): number => {
  if (!block.content) return 1;
  const maxChars = block.type === 'dialogue' ? 35 : block.type === 'parenthetical' ? 42 : 62;
  // Simple word wrap estimation
  const lines = block.content.split('\n').reduce((acc, line) => {
    if (line.length === 0) return acc + 1;
    let count = 0;
    let currentLen = 0;
    const words = line.split(' ');
    for (const w of words) {
      const wLen = w.length;
      if (currentLen + wLen + (currentLen > 0 ? 1 : 0) > maxChars) {
        count++;
        currentLen = wLen;
      } else {
        currentLen += wLen + (currentLen > 0 ? 1 : 0);
      }
    }
    if (currentLen > 0) count++;
    return acc + count;
  }, 0);
  return Math.max(1, lines);
};

export const SFX_KEYWORDS = [
  'SFX', 'SOUND', 'SOM', 'MUSIC', 'MÚSICA', 'MUSIQUE', 'NOISE', 'BARULHO', 'EFEITO', 'EFECTO', 'SONIDO', 'RUIDO', 'BRUIT', 'SON'
];
export const CAMERA_KEYWORDS = [
  'ANGLE', 'ÂNGULO', 'ANGULO', 'CÂMERA', 'CAMERA', 'CÁMARA', 'CADRAGE', 'CLOSE', 'ZOOM', 'PAN', 'TILT', 'TRACKING', 'TRAVELLING', 'POV', 'P.O.V.', 'INSERT', 'ESTABLISHING', 'PLANO', 'OBJECTIF'
];

export const COMMON_TRANSITIONS = [
  'FADE IN:', 'FADE OUT.', 'CUT TO:', 'DISSOLVE TO:', 'JUMP CUT TO:', 'MATCH CUT TO:', 'SMASH CUT TO:',
  'FADE TO BLACK.', 'FADE TO:', 'CUT TO BLACK.', 'TIME CUT:', 'WIPE TO:', 'IRIS IN:', 'IRIS OUT:', 'FREEZE FRAME:',
  'FLASH CUT:', 'CORTA PARA:', 'CORTA PARA', 'CORTE PARA:', 'CORTE PARA', 'FUNDE PARA:', 'FUNDE PARA',
  'DISSOLVE PARA:', 'DISSOLVE PARA', 'CORTE A:', 'FUNDIDO A:', 'FUNDIDO EN NEGRO', 'COUPE SUR:', 'FONDU AU NOIR', 'FONDU ENCHAÎNÉ'
];

export const SFX_SOUNDS = [
  // Common impact sounds
  'THUD', 'CLACK', 'BANG', 'CRASH', 'BOOM', 'POW', 'WHAM', 'ZAP',
  'KNOCK', 'CLICK', 'RING', 'BEEP', 'SLAM', 'KRRR-KRRR',
  // Onomatopeias commonly misclassified as characters
  'CLANG', 'BUM', 'CRAAACK', 'KLANG', 'SWOOSH', 'WHOOSH', 'SPLAT',
  'CRUNCH', 'SCREECH', 'CLUNK', 'THUMP', 'KABOOM', 'CRACK', 'SMACK',
  'POP', 'FIZZ', 'SIZZLE', 'RUMBLE', 'ROAR', 'WHACK', 'BOING',
  'SPLASH', 'DRIP', 'BUZZ', 'HISS', 'GROWL', 'SNAP', 'CREAK',
  'CLAC', 'SCRITCH', 'SCRATCH', 'PFFT', 'PFFFTTTT', 'TOC', 'TIC', 'TAC',
  // Equipment and electronic sounds
  'CHIME', 'WHIRR', 'HUM', 'WHISTLE', 'BEEP', 'ALARM', 'SIREN', 'DRONE', 'PING', 'BUZZER',
  // Vocal sounds in action
  'VOICE', 'WHISPER', 'SHOUT', 'SCREAM', 'CRY', 'LAUGH', 'GIGGLE'
];

export const METADATA_KEYWORDS = [
  'TITULO', 'TÍTULO', 'TITLE',
  'AUTOR', 'AUTHOR',
  'ESCRITO POR', 'WRITTEN BY',
  'POR', 'BY',
  'VERSAO', 'VERSÃO', 'VERSION', 'DRAFT',
  'REVISAO', 'REVISÃO', 'REVISION',
  'CONTATO', 'CONTACT',
  'EMAIL', 'TELEFONE', 'PHONE',
  'LOGLINE', 'LOG_LINE', 'LOG LINE',
  'SINOPSE', 'SYNOPSIS',
  'BASED ON', 'BASEADO EM',
  'BASED ON,', 'BASEADO EM,'
];

export const TIME_MARKERS_PT = [
  "MAIS TARDE", "MAIS CEDO", "MOMENTOS DEPOIS", "EM SEGUIDA", "DEPOIS", "ANTES", "ENTREMENTES", "ENQUANTO ISSO", "NO DIA SEGUINTE", "NA MANHÃ SEGUINTE"
];
export const TRANSITION_MARKERS_PT = [
  "CORTE PARA", "CORTA PARA", "DISSOLVE PARA", "FADE IN", "FADE OUT", "FUNDIR PARA", "FIM"
];
export const TIME_MARKERS_EN = [
  "LATER", "EARLIER", "MOMENTS LATER", "NEXT", "AFTER", "BEFORE", "MEANWHILE", "THE NEXT DAY", "THE FOLLOWING MORNING"
];
export const TRANSITION_MARKERS_EN = [
  "CUT TO", "DISSOLVE TO", "FADE IN", "FADE OUT", "END"
];
export const TIME_MARKERS_ES = [
  "MÁS TARDE", "MÁS TEMPRANO", "MOMENTOS DESPUÉS", "LUEGO", "DESPUÉS", "ANTES", "MIENTRAS TANTO", "AL DÍA SIGUIENTE"
];
export const TRANSITION_MARKERS_ES = [
  "CORTE A", "FUNDIDO A", "FUNDIDO EN NEGRO", "FIN"
];
export const TIME_MARKERS_FR = [
  "PLUS TARD", "PLUS TÔT", "QUELQUES INSTANTS PLUS TARD", "ENSUITE", "APRÈS", "AVANT", "PENDANT CE TEMPS", "LE LENDEMAIN"
];
export const TRANSITION_MARKERS_FR = [
  "COUPE SUR", "FONDU AU NOIR", "FONDU ENCHAÎNÉ", "FIN"
];

export const ALL_MARKERS = [
  ...TIME_MARKERS_PT, ...TRANSITION_MARKERS_PT,
  ...TIME_MARKERS_EN, ...TRANSITION_MARKERS_EN,
  ...TIME_MARKERS_ES, ...TRANSITION_MARKERS_ES,
  ...TIME_MARKERS_FR, ...TRANSITION_MARKERS_FR
];


export const CAMERA_CUE_BLACKLIST: string[] = [];
// Populate blacklist from dictionary
for (const cat in SHOT_CUE_DICTIONARY) {
  for (const key in SHOT_CUE_DICTIONARY[cat]) {
    for (const lang of ["en", "pt", "es", "fr"] as const) {
      SHOT_CUE_DICTIONARY[cat][key][lang].forEach(s => {
        const norm = normalizeText(s);
        if (norm && !CAMERA_CUE_BLACKLIST.includes(norm)) CAMERA_CUE_BLACKLIST.push(norm);
      });
    }
  }
}

export const CHARACTER_BLACKLIST = [
  'INT', 'EXT', 'I/E', 'FADE', 'CUT', 'DISSOLVE', 'MATCH', 'SMASH', 'JUMP', 'TIME', 'TRANSITION',
  'MONTAGEM', 'INSERT', 'SUPER', 'CARTELA', 'SFX',
  'CÂMERA', 'CAMERA', 'ANGLE', 'ÂNGULO', 'ZOOM', 'BEAT', 'PAUSE',
  'SILÊNCIO', 'SILENCIO', 'FIM', 'THE END', 'CONTINUA', 'CONTINUED',
  'CLOSE', 'TRACKING', 'PAN', 'ESTABLISHING', 'STOCK', 'OMITTED', 'SUDDENLY', 'MOMENTS LATER', 'LATER', 'DAY', 'NIGHT',
  'SOUND', 'SOM', 'NOISE', 'BARULHO',
  ...ALL_MARKERS,
  ...CAMERA_CUE_BLACKLIST
];

export const COMMON_INANIMATE_OBJECTS = [
  'PEDRA', 'ROCHA', 'CASCALHO', 'MESA', 'CADEIRA', 'PORTA', 'JANELA', 'ARMA', 'FACA', 'ESPADA',
  'CARRO', 'CAMINHÃO', 'TRATOR', 'TRACTOR', 'VEÍCULO', 'NAVE', 'COMPUTADOR', 'TELA', 'MONITOR', 'CELULAR', 'TELEFONE',
  'RELÓGIO', 'COPO', 'GARRAFA', 'PRATO', 'TALHER', 'CANETA', 'PAPEL', 'REVOLVER', 'PISTOLA', 'FUZIL',
  'LANTERNA', 'MOCHILA', 'BOLSA', 'MALA', 'FOTO', 'FOTOGRAFIA', 'QUADRO', 'ESPELHO', 'RADIO', 'RÁDIO',
  'JORNAL', 'REVISTA', 'LIVRO', 'CARTA', 'BILHETE', 'COLAR', 'ANEL', 'PULSEIRA', 'CHAVE', 'ISQUEIRO',
  'CIGARRO', 'CHARUTO', 'CACHIMBO', 'FOGÃO', 'GELADEIRA', 'PIO', 'LÂMPADA', 'LAMPADA',
  'CHÃO', 'CHAO', 'POEIRA', 'FUMAÇA', 'FUMACA', 'SANGUE', 'CHUVA', 'VENTO', 'EXPLOSÃO', 'EXPLOSAO', 'METEORO',
  'ESCADA', 'ELEVADOR', 'CORRIMÃO', 'TAPETE', 'CORTINA', 'SOFÁ', 'POLTRONA', 'CAMA', 'TRAVESSEIRO',
  'LENÇOL', 'COBERTOR', 'TOALHA', 'SABONETE', 'ESCOVA', 'PENTE', 'ESPONJA', 'VASSOURA', 'RODO',
  'BALDE', 'PANO', 'SACO', 'LIXEIRA', 'LIXO', 'CAIXA', 'PACOTE', 'ENVELOPE',
  // Generics
  'OBJETO', 'ITEM', 'COISA', 'ELEMENTO', 'PEÇA', 'PARTE', 'FRAGMENTO', 'ESFERA',
  // Tech/Sci-Fi
  'DISPOSITIVO', 'ARTEFATO', 'MECANISMO', 'ESTRUTURA', 'EQUIPAMENTO', 'MÁQUINA', 'ROBÔ', 'DRONE', 'CABO', 'FIO',
  // Nature
  'FLOR', 'ÁRVORE', 'PLANTA', 'FRUTO', 'SEMENTE', 'RAIZ', 'TRONCO', 'GALHO', 'FOLHA', 'ESPINHO', 'RESINA', 'CIPO', 'CIPÓ',
  // Substances
  'SOLUÇÃO', 'SUBSTÂNCIA', 'LÍQUIDO', 'GÁS', 'MATERIAL', 'FLUIDO', 'POEIRA', 'FUMAÇA', 'VAPOR', 'ÁGUA', 'SANGUE',
  // Infrastructure
  'PAREDE', 'PISO', 'TETO', 'CHÃO', 'SOLO', 'ESTRADA', 'RUA', 'CAMINHO', 'PORTÃO', 'CERCA', 'MURO', 'PONTE'
];

export const ABSTRACT_BLACKLIST = new Set([
  'DETALHE', 'DETALHES', 'ESCALA', 'ESCALAS', 'COISA', 'COISAS', 'ALGO', 'ISSO', 'AQUILO',
  'IDEIA', 'IDEIAS', 'SENSACAO', 'SENSACÃO', 'SENSACÕES', 'SENSACÕES', 'MOMENTO', 'MOMENTOS',
  'SITUACAO', 'SITUACÃO', 'SITUACÕES', 'SITUACÕES', 'PROCESSO', 'PROCESSOS', 'FORMA', 'FORMAS',
  'JEITO', 'JEITOS', 'ASPECTO', 'ASPECTOS', 'CONTEXTO', 'CONTEXTOS', 'MOTIVO', 'MOTIVOS',
  'RAZAO', 'RAZÃO', 'RAZÕES', 'RAZOES', 'PARTE', 'PARTES', 'PONTO', 'PONTOS', 'NÍVEL', 'NIVEL',
  'VISAO', 'VISÃO', 'VISÕES', 'VISOES', 'PERCEPCAO', 'PERCEPCÃO', 'PERCEPCÕES', 'PERCEPCÕES',
  'NOS', 'NESSA', 'NESSE', 'AQUELA', 'AQUELE', 'MUITO', 'MUITOS', 'MUITA', 'MUITAS', 'TUDO', 'NADA',
  'EXEMPLO', 'EXEMPLOS', 'CASO', 'CASOS', 'TIPO', 'TIPOS', 'CLASSE', 'CLASSES',
  'COM', 'SEM', 'POR', 'PARA', 'ATÉ', 'ATE', 'DESDE', 'COMO',
  'PRECEDES', 'FOLLOWS', 'STARTS', 'BEGINS', 'MOVES', 'GOES', 'COMES', 'THAT', 'WHICH', 'WHO',
  'COMEÇA', 'TERMINA', 'ABRE', 'FECHA', 'PEGA', 'OLHA', 'VÊ', 'MOVE', 'SAI', 'ENTRA', 'SENTA', 'LEVANTA', 'CORRE', 'PULA', 'CAI'
]);

export const STRONG_SINGLE_WORDS = new Set([
  'CHÃO', 'CHAO', 'POEIRA', 'FUMAÇA', 'FUMACA', 'SANGUE', 'CHUVA', 'VENTO', 'EXPLOSÃO', 'EXPLOSAO',
  'METEORO', 'CÂMERA', 'CAMERA', 'ZOOM', 'DOLLY', 'FADE', 'CORTE', 'CUT', 'TRACK', 'PAN', 'TILT'
]);

export const GROUP_CHARACTER_LEXICON_PT = [
  "OPERÁRIAS", "OPERARIOS", "SOLDADOS", "GUARDAS", "SENTINELAS", "TRABALHADORES", "FUNCIONÁRIOS",
  "CLIENTES", "CRIANÇAS", "MULHERES", "HOMENS", "PESSOAS", "MULTIDÃO", "TURBA", "PLATEIA", "EQUIPE",
  "POLICIAIS", "BOMBEIROS", "ENFERMEIROS", "MÉDICOS"
];

/**
 * Article prefixes in multiple languages.
 * Lines starting with these words are likely descriptive sub-headings, not character names.
 * e.g. "THE EYE", "A MOMENT LATER", "O OLHO", "UNE VOITURE"
 */
export const ARTICLE_PREFIXES = [
  // English
  'THE', 'A', 'AN',
  // Portuguese
  'O', 'OS', 'AS', 'UM', 'UMA', 'UNS', 'UMAS',
  // Spanish
  'EL', 'LA', 'LOS', 'LAS', 'UN', 'UNA', 'UNOS', 'UNAS',
  // French
  'LE', 'LA', 'LES', 'UN', 'UNE', 'DES'
];

export const AGENT_VERBS_PT = [
  "SOBEM", "DESCEM", "CORREM", "ENTRAM", "SAEM", "GUARDAM", "VIGIAM", "CARREGAM", "OBSERVAM",
  "GRITAM", "ATACAM", "CERCAM", "AVANÇAM", "RECUAM", "SE APROXIMAM", "REAGEM", "ANDAM", "CAMINHAM",
  "LUTAM", "CONVERSAM", "FALAM", "OLHAM", "ESPERAM"
];

// Unified Ignore List for Analysis (Props/Characters)
export const SHARED_IGNORE_LIST = new Set([
  ...CHARACTER_BLACKLIST,
  // Common Stopwords & Prepositions (EN/PT)
  'THE', 'AND', 'BUT', 'THEN', 'WITH', 'FROM', 'FOR', 'THAT', 'THIS',
  'O', 'A', 'OS', 'AS', 'UM', 'UMA', 'UNS', 'UMAS',
  'DE', 'DO', 'DA', 'DOS', 'DAS', 'EM', 'NO', 'NA', 'NOS', 'NAS',
  'POR', 'PELO', 'PELA', 'PARA', 'COM', 'SEM', 'QUE', 'SE',
  'AO', 'AOS', 'DENTRO', 'FORA', 'LADO', 'CIMA', 'BAIXO',
  'AGORA', 'DEPOIS', 'MOMENTOS', 'CONTINUA', 'MESMO', 'TEMPO',
  'E', 'OU',
  // Script Terms
  'CENA', 'ROTEIRO', 'PÁGINA', 'PAGINA', 'ATO', 'FIM', 'INICIO', 'INÍCIO', 'MEIO',
  'DIA', 'NOITE', 'TARDE', 'MANHÃ', 'MANHA', 'MADRUGADA', 'DAWN', 'DUSK', 'CONTINUOUS', 'SAME TIME',
  'CORTE', 'CORTA', 'DISSOLVE', 'FADE', 'FUNDE',
  'SOM', 'MUSICA', 'MÚSICA', 'TRILHA', 'EFEITO', 'VFX', 'CGI',
  'PLANO', 'DETALHE', 'GERAL', 'CONJUNTO', 'AMERICANO', 'PRIMEIRO', 'CLOSE', 'WIDE SHOT', 'POV',
  'LETREIRO', 'TEXTO', 'LEGENDA', 'CRÉDITOS', 'CREDITOS', 'TITLE', 'CARD', 'BLACK', 'SCREEN',
  'METADADOS', 'METADATA', 'ANÁLISE', 'ANALYSIS', 'CINEMA', 'FILME',
  'TITLE', 'AUTHOR', 'AUTOR', 'TÍTULO',
  ...CAMERA_CUE_BLACKLIST
]);

export const ENVIRONMENT_TERMS = [
  'SALA', 'COZINHA', 'QUARTO', 'BANHEIRO', 'VARANDA', 'JARDIM', 'QUINTAL',
  'RUA', 'AVENIDA', 'ESTRADA', 'RODOVIA', 'BECO', 'TRAVESSA', 'PRAÇA',
  'FLORESTA', 'MATA', 'BOSQUE', 'SELVAM', 'DESERTO', 'PRAIA', 'MAR', 'OCEANO',
  'RIO', 'LAGO', 'LAGOA', 'CACHOEIRA', 'FONTE', 'POÇO',
  'MONTANHA', 'MORRO', 'COLINA', 'VALE', 'CAVERNA', 'GRUTA', 'TÚNEL',
  'CIDADE', 'VILA', 'POVOADO', 'ALDEIA', 'FAZENDA', 'SÍTIO',
  'ESCOLA', 'UNIVERSIDADE', 'COLÉGIO', 'BIBLIOTECA', 'MUSEU',
  'HOSPITAL', 'CLÍNICA', 'FARMÁCIA', 'CONSULTÓRIO',
  'DELEGACIA', 'PRISÃO', 'CADEIA', 'CELA', 'TRIBUNAL',
  'LOJA', 'MERCADO', 'SUPERMERCADO', 'SHOPPING', 'PADARIA', 'AÇOUGUE', 'BAR', 'RESTAURANTE',
  'HOTEL', 'MOTEL', 'POUSADA', 'HOSTEL',
  'IGREJA', 'TEMPLO', 'CATEDRAL', 'CAPELA', 'SANTUÁRIO',
  'ESCRITÓRIO', 'GABINETE', 'SALA DE REUNIÃO', 'FIRMA', 'EMPRESA',
  'FÁBRICA', 'USINA', 'GALPÃO', 'ARMAZÉM', 'DEPÓSITO',
  'ESTAÇÃO', 'AEROPORTO', 'RODOVIÁRIA', 'PORTO', 'CAIS', 'METRÔ', 'TREM', 'ÔNIBUS',
  'CARRO', 'CAMINHÃO', 'VEÍCULO', 'AUTOMÓVEL', 'TAXI', 'UBER', 'NAVIO', 'BARCO', 'LANCHA', 'AVIÃO', 'HELICÓPTERO', 'NAVE',
  'QUARTEL', 'BASE', 'BUNKER', 'REFÚGIO', 'ABRIGO',
  'ESTÚDIO', 'TEATRO', 'CINEMA', 'AUDITÓRIO', 'PALCO', 'BASTIDORES',
  'ACADEMIA', 'GINÁSIO', 'QUADRA', 'ESTÁDIO', 'CAMPO', 'PISCINA', 'CLUBE',
  'PARQUE', 'BOSQUE', 'ZOOLÓGICO', 'JARDIM_BOTÂNICO'
];

export const LOCATION_BLACKLIST = [
  'MENTE', 'CABEÇA', 'MÃO', 'MÃOS', 'PÉ', 'PÉS', 'BRAÇO', 'PERNA', 'OLHOS', 'OLHO', 'ROSTO', 'CARA', 'CORPO',
  'LADO', 'CIMA', 'BAIXO', 'FRENTE', 'TRÁS', 'ESQUERDA', 'DIREITA', 'CENTRO', 'MEIO', 'FUNDO',
  'ALTO', 'BAIXO', 'DENTRO', 'FORA',
  'LONGE', 'PERTO',
  'AGORA', 'HOJE', 'ONTEM', 'AMANHÃ',
  'TEMPO', 'MOMENTO', 'INSTANTE',
  'VIDA', 'MORTE', 'ALMA', 'ESPÍRITO',
  'MOVIMENTO', 'AÇÃO', 'REAÇÃO',
  'PERFEITA', 'SUA', 'SEU', 'MEU', 'MINHA', 'NOSSO', 'NOSSA', // Pronouns/Adjectives
  'GRANDE', 'PEQUENO', 'LONGO', 'CURTO', // Adjectives
  'APENAS', 'SOMENTE', 'TUDO', 'NADA',
  'ENTRADA', 'SAÍDA', // Usually parts of a location, not location itself unless specific (ENTRADA DO METRO)
  'INTERIOR', 'EXTERIOR'
];

/**
 * Extracts potential locations from Action lines using Confidence Scoring.
 * 
 * Scoring Rules:
 * - Base Score: 0
 * - Found in ENVIRONMENT_TERMS: +0.6
 * - Preceded by Locative Preposition (NO, NA, EM...): +0.4
 * - Multi-word (2+ words): +0.3
 * - Explicit Blacklist match: Score = 0
 * - Single word NOT in Environment matches: -0.2 (Penalty for random CAPS words)
 * 
 * Threshold: >= 0.75
 */
export const extractLocationsFromAction = (text: string): string[] => {
  const found: Set<string> = new Set();
  // DO NOT upperCase the whole text, otherwise we lose the "Capitalization" signal!

  // Regex to find originally capitalized phrases
  // We look for: Word starting with Uppercase, followed by optional Uppercase words.
  // Limitation: "A SALA" -> "A" is Up, "SALA" is Up. Regex matches "A SALA"? 
  // Regex: \b[A-ZÀ-Ú0-9][A-ZÀ-Ú0-9]*(?:\s+[A-ZÀ-Ú0-9]+)*\b
  // But strictly, we want "SALA" (all caps) or "Sala" (Title case)? 
  // User says "LOCAÇÕES... aparecem no corpo...". Usually Scene Headings are UPPER.
  // In Action, they might be UPPER (slugs embedded) or "SALA" (Env term).
  // Implicit rule: Locations in Action are usually CAPITALIZED to distinct them.
  // If they are just "Eu vou para a sala.", it's usually not extracted as a Location Asset. 
  // So we stick to CAPS detection.

  const candidatesRegex = /\b[A-ZÀ-Ú0-9]{2,}(?:\s+[A-ZÀ-Ú0-9]{2,})*\b/g;
  let match;

  while ((match = candidatesRegex.exec(text)) !== null) {
    const candidate = match[0].trim();
    if (!candidate) continue;

    // Normalize for checks
    const upperCandidate = candidate.toUpperCase();
    const startIndex = match.index;

    // 0. Filter Sanity
    if (upperCandidate.length < 3) continue; // Skip 2 letter words like "OS", "AS", unless context? NO.
    if (SHARED_IGNORE_LIST.has(upperCandidate)) continue;
    if (LOCATION_BLACKLIST.includes(upperCandidate)) continue;
    if (LOCATION_BLACKLIST.includes(upperCandidate.split(' ')[0])) continue; // "LADO ESQUERDO" -> LADO is blacklist.

    let score = 0;

    // 1. Environment Term Check
    // Check if the candidate IS or CONTAINS an environment term
    const envMatch = ENVIRONMENT_TERMS.find(term => upperCandidate === term || upperCandidate.includes(` ${term} `) || upperCandidate.startsWith(`${term} `) || upperCandidate.endsWith(` ${term}`));
    if (envMatch) {
      if (upperCandidate === envMatch) score += 0.8; // Strong match
      else score += 0.6; // Partial match (e.g. "SALA DE JANTAR" contains "SALA")
    } else {
      // Penalty for random CAPS words
      if (!upperCandidate.includes(' ')) score -= 0.3;
    }

    // 2. Preposition Context
    // Look at text BEFORE the match (in original text, then uppercase for comparison)
    const upperText = text.toUpperCase();
    const preText = upperText.substring(0, startIndex).trim();
    const locativePreps = ['NO', 'NA', 'NOS', 'NAS', 'EM', 'NUM', 'NUMA', 'AO', 'AOS', 'DO', 'DA', 'DOS', 'DAS', 'PARA'];
    const lastWord = preText.split(/[\s,.]+/).pop() || '';

    if (locativePreps.includes(lastWord)) {
      score += 0.4;
    } else if (preText.endsWith('ENTRA NO') || preText.endsWith('SAI DO') || preText.endsWith('VAI PARA')) {
      // Stronger verb context?
    }

    // 3. Structure
    if (upperCandidate.includes(' ')) {
      const words = upperCandidate.split(' ').length;
      if (words >= 2) score += 0.3;
    }

    // 4. Blacklist Check (Safety)
    if (LOCATION_BLACKLIST.some(b => upperCandidate === b)) score = 0;

    // Threshold Check
    if (score >= 0.75) {
      found.add(upperCandidate);
    }
  }

  return Array.from(found);
};

export interface CandidateElement {
  label: string;
  suggestedCategory: 'CHARACTER' | 'LOCATION' | 'PROP' | 'SFX_VFX' | 'CAMERA' | 'TRANSITION';
}

export const extractElementCandidatesFromText = (text: string): CandidateElement[] => {
  const candidates: CandidateElement[] = [];
  const foundLabels = new Set<string>();

  const upperText = text.toUpperCase();

  const addCandidate = (label: string, category: CandidateElement['suggestedCategory']) => {
    const normalized = label.trim();
    const key = normalized.toUpperCase();
    if (normalized.length < 2) return;

    // Rule 1: ABSTRACT_BLACKLIST filter (on the whole label)
    if (ABSTRACT_BLACKLIST.has(key)) return;

    // Check if any individual word is in the abstract blacklist 
    // This prevents "detalhe Nessa" if it somehow slips through the phrase builder
    const phraseWords = key.split(/\s+/);
    // Connectives like 'de', 'da' are allowed, but other abstract words are not.
    // We already have a connectives set in the outer scope? No, it's inside the loop.
    // Let's use a local set or the outer one if I move it.
    const localConnectives = new Set(['DE', 'DA', 'DO', 'DAS', 'DOS', 'COM', 'SEM', 'POR', 'PARA']);
    if (phraseWords.some(w => ABSTRACT_BLACKLIST.has(w) && !localConnectives.has(w))) return;

    // Generic stopword filter (shorter but same effect as larger lists)
    if (['PARA', 'SOBRE', 'NESSA', 'NESSE', 'AQUELA', 'AQUELE', 'ESTA', 'ESTE', 'COM', 'SEM'].includes(key)) return;

    if (!foundLabels.has(key)) {
      foundLabels.add(key);

      // Auto-categorize PROPs as SFX if they contain sound keywords
      let refinedCategory = category;
      if (category === 'PROP' || category === 'CHARACTER') {
        const upLabel = normalized.toUpperCase();
        // Check if it's a known sound keyword or ends with one
        if (SFX_SOUNDS.some(s => upLabel === s || upLabel.endsWith(' ' + s) || upLabel.includes(' ' + s + ' '))) {
          refinedCategory = 'SFX_VFX';
        }
      }

      candidates.push({ label: normalized, suggestedCategory: refinedCategory });
    }
  };

  // 1. CAMERA detection
  // 1a. Dictionary-based (Multilingual) - Enhanced to capture modifiers like "SUPER" or "(MACRO)"
  for (const cat in SHOT_CUE_DICTIONARY) {
    for (const key in SHOT_CUE_DICTIONARY[cat]) {
      const entry = SHOT_CUE_DICTIONARY[cat][key];
      const allPhrases = [...entry.en, ...entry.pt, ...entry.es, ...entry.fr];
      allPhrases.forEach(phrase => {
        if (phrase.length < 3) return;

        // Stricter filtering for generic dictionary terms used alone
        const upPhrase = phrase.toUpperCase();
        if (ABSTRACT_BLACKLIST.has(upPhrase) && !text.toUpperCase().includes('PLANO ' + upPhrase)) return;

        // Match phrase with optional modifiers (e.g. SUPER CLOSE-UP) or descriptions (e.g. (MACRO))
        const EscapedPhrase = phrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        // Look for: Modifier? + Phrase + (Parens)?
        const regex = new RegExp(`(?:\\b(?:SUPER|EXTRA|ULTRA|EXTREME|PLANO)\\s+)?\\b${EscapedPhrase}\\b(?:\\s*\\([^)]+\\))?`, 'i');

        const m = text.match(regex);
        if (m) {
          addCandidate(m[0].trim(), 'CAMERA');
        }
      });
    }
  }

  // 1b. Heuristic fallback: "CÂMERA" + Verb
  const cameraVerbs = ['SOBE', 'DESCE', 'APROXIMA', 'AFASTA', 'GIRA', 'PAN', 'TILT', 'ZOOM', 'SEGUE', 'ACOMPANHA', 'RECU recua', 'AVANÇA'];
  const cameraRegex = new RegExp(`\\b(?:CÂMERA|CAMERA)\\s+(?:${cameraVerbs.join('|')})\\b`, 'gi');
  let match;
  while ((match = cameraRegex.exec(text)) !== null) {
    addCandidate(match[0].toUpperCase(), 'CAMERA');
  }

  // 1c. Keyword-based fallback
  CAMERA_KEYWORDS.forEach(keyword => {
    if (upperText.includes(keyword)) {
      // Find the whole phrase containing the keyword if it's in CAPS
      const regex = new RegExp(`\\b[A-ZÀ-Ú0-9]*${keyword}[A-ZÀ-Ú0-9]*(?:\\s+[A-ZÀ-Ú0-9]+)*\\b`, 'i');
      const m = text.match(regex);
      if (m) {
        addCandidate(m[0].trim(), 'CAMERA');
      } else {
        addCandidate(keyword, 'CAMERA');
      }
    }
  });

  // 2. TRANSITIONS detection
  COMMON_TRANSITIONS.forEach(trans => {
    const cleanTrans = trans.replace(/[:\.]/g, '');
    if (upperText.includes(cleanTrans)) {
      addCandidate(cleanTrans, 'TRANSITION');
    }
  });

  // 3. SFX detection
  SFX_SOUNDS.forEach(sound => {
    const soundRegex = new RegExp(`\\b${sound}\\b`, 'i');
    if (soundRegex.test(text)) {
      addCandidate(sound, 'SFX_VFX');
    }
  });
  const sfxClues = [
    /SOM DE ([^.,\n]+)/i,
    /BARULHO DE ([^.,\n]+)/i,
    /SFX[:\s]([^.,\n]+)/i,
    /VFX[:\s]([^.,\n]+)/i,
    /SOUND OF ([^.,\n]{2,30})/i,
    /NOISE OF ([^.,\n]{2,30})/i
  ];
  sfxClues.forEach(regex => {
    const m = regex.exec(text);
    if (m && m[1]) {
      const label = m[1].trim();
      addCandidate(label, 'SFX_VFX');
    }
  });

  // 3b. Inline SFX markers (*...*, (...), [...])
  const inlineSfx = extractInlineSfx(text);
  inlineSfx.forEach(s => addCandidate(s, 'SFX_VFX'));

  // 4. LOCATIONS detection
  if (/\b(?:INT|EXT|I\/E)\./i.test(text)) {
    const locMatch = text.match(/^(?:INT\.|EXT\.|I\/E)\s+([^-–\n]+)/i);
    if (locMatch && locMatch[1]) {
      addCandidate(locMatch[1].trim().toUpperCase(), 'LOCATION');
    }
  }
  ENVIRONMENT_TERMS.forEach(env => {
    const regex = new RegExp(`\\b(?:NA|NO|EM|NUM|NUMA)\\s+${env}\\b`, 'i');
    if (regex.test(text)) {
      addCandidate(env, 'LOCATION');
    }
  });

  // 5. CHARACTERS / PROPS
  // 5a. Noun Phrases (Props) - Artigos + Substantivos (Highest priority for props)
  // Captures: "A bota", "Uma bota de borracha", "O grande livro", "Um talo rígido"
  const tokens = text.split(/\s+/);
  // Enhanced articles set with contractions (Rule: Portuguese NO, NA, DO, DA trigger extraction)
  const articlesSet = new Set(['O', 'A', 'OS', 'AS', 'UM', 'UMA', 'UNS', 'UMAS', 'THE', 'AN', 'NO', 'NA', 'NOS', 'NAS', 'DO', 'DA', 'DOS', 'DAS', 'PELO', 'PELA']);
  // Connectives that are common parts of object names in Portuguese
  const connectives = new Set(['de', 'da', 'do', 'das', 'dos']);

  for (let i = 0; i < tokens.length - 1; i++) {
    const word = tokens[i].replace(/[.,:;!?]$/, '');
    if (articlesSet.has(word.toUpperCase())) {
      let phraseWords = [];
      let j = i + 1;
      let lastWasConnective = false;

      while (j < tokens.length && j < i + 5) {
        const rawToken = tokens[j];
        const nextWord = rawToken.replace(/[.,:;!?]$/, '');
        if (!nextWord) break;

        const isCapitalized = /^[A-ZÀ-Ú]/.test(nextWord);
        const isConnective = connectives.has(nextWord.toLowerCase());

        // Stop if the word itself is abstract/genric and it's the start
        if (j === i + 1 && ABSTRACT_BLACKLIST.has(nextWord.toUpperCase())) break;

        // Rule 2: Capture first word always, OR if Capitalized, OR if connective, OR following a connective, OR just short descriptive phrases
        if (j === i + 1 || isCapitalized || isConnective || (lastWasConnective && nextWord.length >= 2) || (j <= i + 3 && !ABSTRACT_BLACKLIST.has(nextWord.toUpperCase()) && !nextWord.endsWith('s') && !nextWord.endsWith('ing'))) {
          phraseWords.push(nextWord);
          lastWasConnective = isConnective;

          // CRITICAL: Stop if word has terminal punctuation
          if (/[.!?]$/.test(rawToken)) break;

          j++;
        } else {
          break;
        }
      }

      const rawPhrase = phraseWords.join(' ').trim();
      if (rawPhrase.length >= 3) {
        if (SHARED_IGNORE_LIST.has(rawPhrase.toUpperCase())) continue;
        addCandidate(rawPhrase, 'PROP');
      }
    }
  }

  // 5b. Strict CAPS/Alphanumeric for robust IDs
  const strictIdRegex = /\b[A-Z0-9]+-[A-Z0-9-]+\b/g;
  while ((match = strictIdRegex.exec(text)) !== null) {
    addCandidate(match[0], 'CHARACTER');
  }

  // 5c. Standard CAPS phrases
  const capsRegex = /\b[A-ZÀ-Ú0-9]{2,}(?:\s+[A-ZÀ-Ú0-9]{2,})*\b/g;
  while ((match = capsRegex.exec(text)) !== null) {
    const phrase = match[0];
    if (phrase.length < 3 && !phrase.includes('-')) continue;
    if (SHARED_IGNORE_LIST.has(phrase)) continue;
    if (foundLabels.has(phrase)) continue;
    if (COMMON_TRANSITIONS.includes(phrase)) continue;
    if (SFX_SOUNDS.includes(phrase)) continue;
    addCandidate(phrase, 'CHARACTER');
  }

  // 5e. Rule 4: Metaphor Detection ("É um X")
  // Accept X only if visualizable phenomenon or object
  const metaphorRegex = /(?:É|E)\s+(?:UM|UMA)\s+([A-ZÀ-Úa-zà-ú\s]+?)(?:[.,:;!?]|$)/gi;
  let metaphorMatch;
  while ((metaphorMatch = metaphorRegex.exec(text)) !== null) {
    const x = metaphorMatch[1].trim();
    const xUpper = x.toUpperCase();
    // If X is abstract, addCandidate filter will block it.
    // If it's a strong word or common object, it gets through.
    if (x.length >= 3 && !ABSTRACT_BLACKLIST.has(xUpper)) {
      addCandidate(x, 'PROP'); // Default to prop/fx for metaphors
    }
  }

  // 5f. Rule 3: Strong Single Words Pass
  tokens.forEach(token => {
    const cleanToken = token.replace(/[.,:;!?]$/, '').toUpperCase();
    if (STRONG_SINGLE_WORDS.has(cleanToken)) {
      addCandidate(cleanToken, 'PROP');
    }
  });

  return candidates;
};

export const SCENE_HEADINGS_START = ['INT.', 'EXT.', 'I/E', 'INT ', 'EXT ', 'CENA ', 'SCENE ', 'EST.', 'FLASHBACK -', 'FLASHFORWARD -', 'SONHO -', 'PESADELO -', 'DREAM -', 'NIGHTMARE -', 'SUEÑO -', 'PESADILLA -', 'RÊVE -', 'CAUCHEMAR -'];

export const splitMetadataAndParse = (rawText: string): { metadata: string, blocks: ScriptBlock[] } => {
  const scriptMarkers = ['INT.', 'EXT.', 'I/E', 'CENA ', 'SCENE ', 'FADE IN', 'FLASHBACK -', 'FLASHFORWARD -', 'SONHO -', 'PESADELO -', 'DREAM -', 'NIGHTMARE -', 'SUEÑO -', 'PESADILLA -', 'RÊVE -', 'CAUCHEMAR -'];
  const metadataKeywords = ['CLASSIFICAÇÃO', 'ANÁLISE', 'VERIFICAÇÃO', 'PILAR', 'PLANT AND PAYOFF', 'STATUS'];

  const lines = rawText.split(/\r?\n/);
  let metadataLines: string[] = [];
  let scriptStartIndex = -1;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    const cleanLine = line.replace(/<\/?center>/gi, "").replace(/<\/?b>/gi, "").replace(/<\/?i>/gi, "").replace(/^[\*\_]+|[\*\_]+$/g, '').trim();
    const lineUpper = cleanLine.toUpperCase();

    if (!line) {
      if (scriptStartIndex === -1) metadataLines.push(lines[i]);
      continue;
    }

    const isScriptBodyMarker = scriptMarkers.some(marker => lineUpper.startsWith(marker));
    const isAIPreamble = lineUpper.includes('AQUI ESTÁ') || lineUpper.includes('ROTEIRO ATUALIZADO') || lineUpper.includes('[SCRIPT]');
    const isMetadata = metadataKeywords.some(key => lineUpper.includes(key));

    if (isScriptBodyMarker && scriptStartIndex === -1 && !isMetadata) {
      scriptStartIndex = i;
      break;
    }

    if (isAIPreamble && scriptStartIndex === -1) {
      scriptStartIndex = i + 1;
      if (line.includes(':') && scriptMarkers.some(m => lineUpper.split(':')[1]?.trim().startsWith(m))) {
        scriptStartIndex = i;
      }
      break;
    }

    metadataLines.push(lines[i]);
  }

  if (scriptStartIndex === -1) {
    for (let i = 0; i < lines.length; i++) {
      const lineUpper = lines[i].trim().toUpperCase();
      if (lineUpper && !metadataKeywords.some(key => lineUpper.includes(key))) {
        scriptStartIndex = i;
        break;
      }
    }
  }

  const scriptText = scriptStartIndex !== -1 ? lines.slice(scriptStartIndex).join('\n').trim() : '';
  const metadataText = metadataLines.join('\n').trim();

  if (!scriptText && rawText) {
    return {
      metadata: '',
      blocks: parseTextToBlocks(rawText)
    };
  }

  return {
    metadata: metadataText,
    blocks: parseTextToBlocks(scriptText)
  };
};

/**
 * Detects if a line is an isolated onomatopoeia.
 * Rules:
 * - ALL CAPS
 * - 2-12 characters (or max 2 short words)
 * - No colons
 * - Optionally ends with ! or .
 * - NOT 3+ words (excludes scene descriptions like "INSERTO - VISÃO MICROSCÓPICA")
 */
export const detectIsolatedOnomatopoeia = (line: string): boolean => {
  const trimmed = line.trim();
  if (!trimmed) return false;

  // No colons allowed
  if (trimmed.includes(':')) return false;

  // Strip markers for checking
  const cleanMarkers = trimmed.replace(/^[\*\[\(\—\–\-]+|[\*\]\)\—\–\-\.\,\;\:\!\s]+$/g, '').trim();
  if (!cleanMarkers) return false;

  const upperClean = cleanMarkers.toUpperCase();

  // If explicitly in our expanded white-list, it's valid
  if (SFX_SOUNDS.includes(upperClean)) return true;

  // Safeguard: Onomatopoeias are usually short (max 2 words or 20 chars)
  // This prevents dialogue like "ISSO É MUITO BOM!" from being misclassified as sounds.
  const words = upperClean.split(/\s+/);
  if (words.length > 2 || upperClean.length > 20) return false;

  // Check for common language words that indicate dialogue rather than sound
  const commonLanguageWords = ['O', 'A', 'E', 'É', 'DO', 'DA', 'DE', 'UM', 'UMA', 'OS', 'AS', 'NO', 'NA', 'EU', 'ME', 'SI', 'SE', 'COM'];
  if (words.some(w => commonLanguageWords.includes(w))) return false;

  // STRICTER HEURISTICS for unknown sounds:

  // 1. Check for 3+ repeated characters (e.g. "ZZZ", "BOOOOM", "CRAAACK", "Psssss")
  // Exclude common words like "SEE", "TOO", "ALL", "OFF"
  const commonThreeLetterRepeats = ['ALL', 'OFF', 'SEE', 'TOO', 'EZZ', 'OSS'];
  if (/(.)\1{2,}/i.test(cleanMarkers) && !commonThreeLetterRepeats.includes(upperClean)) return true;

  // 2. Ends with "!" (common for sounds)
  if (trimmed.endsWith('!')) return true;

  // 3. Sound-like endings (CK, SH, ZZ, TH, CH, FF) - common in onomatopoeias
  if (/(CK|SH|ZZ|TH|CH|FF)$/i.test(upperClean)) return true;

  // 4. Must be ALL CAPS for generic single words (to avoid normal action verbs)
  // UNLESS it has repeated characters (checked above) or is in SFX_SOUNDS (checked above)
  const hasLowerCase = /[a-zà-öø-ÿ]/.test(cleanMarkers);
  if (hasLowerCase) return false;

  return false;
};

export const normalizeSfx = (token: string): string => {
  return token.replace(/^[\*\[\(\—\–\-\s]+|[\*\]\)\—\–\-\.\,\;\:\!\s]+$/g, '').trim().toUpperCase();
};

export const isSfxToken = (token: string): boolean => {
  const clean = normalizeSfx(token);
  // basic check
  if (SFX_SOUNDS.includes(clean)) return true;
  if (detectIsolatedOnomatopoeia(clean)) return true;
  return false;
};

export const extractInlineSfx = (text: string): string[] => {
  const found: Set<string> = new Set();
  const upper = text.toUpperCase();

  // 1. Explicit Markers: *SOUND*, (SOUND), [SOUND], - SOUND -
  // UPDATED: Allowed '.' in character class and increased max length to 60 to support "THUMP... THUMP... THUMP..."
  // Also allowed lowercase to support "Pssssssssshhhhhhh..."
  const markerPatterns = [
    /\*([A-ZÀ-Úa-zà-öø-ÿ0-9\-\.\s]{2,60})\*/g,
    /\(([A-ZÀ-Úa-zà-öø-ÿ0-9\-\.\s]{2,60})\)/g,
    /\[([A-ZÀ-Úa-zà-öø-ÿ0-9\-\.\s]{2,60})\]/g,
    /[—–-]\s*([A-ZÀ-Úa-zà-öø-ÿ0-9\-\.\s]{2,60})\s*[—–-]/g,
    // Add unclosed marker at end of string or line
    /\*([A-ZÀ-Úa-zà-öø-ÿ0-9\-\.\s]{2,60})$/gm
  ];

  markerPatterns.forEach(regex => {
    let match;
    // Note: We use the original text for regex matching if possible, but the local 'text' variable is already case-preserved?
    // Wait, extractInlineSfx receives 'text' which is the original case.
    // In current implementation, it uses 'upper' for context matching? 
    // No, wait. markerPatterns were using [A-Z...].
    // I updated them to [A-Z...a-z...].

    while ((match = regex.exec(text)) !== null) {
      const content = match[1].trim();
      const upperContent = content.toUpperCase();

      // Verify if valid sound
      // 1. Is it a known single sound or matches heuristics?
      if (detectIsolatedOnomatopoeia(content) || SFX_SOUNDS.includes(upperContent)) {
        found.add(upperContent);
        continue;
      }

      // 2. Is it a repetition of known sounds? (e.g. "THUMP... THUMP...")
      const parts = upperContent.split(/[^A-ZÀ-Ú0-9]+/).filter(p => p.length > 0);
      if (parts.length > 0 && parts.every(p => SFX_SOUNDS.includes(p) || p === 'SOM' || p === 'SOUND')) {
        found.add(upperContent);
      }
    }
  });

  // 2. Contextual Sound Verbs
  // Improved Regex: Non-greedy gap, supports optional prepositions (DE, DO, UM...) and English (OF)
  const soundContextRegex = /\b(?:SOM|BARULHO|RUÍDO|ESTALIDO|ESTRONDO|IMPACTO|BATIDA|RANGIDO|CLIQUE|CRACK|SOUND|NOISE)\b(?:\s+(?:DE|DO|DA|DOS|DAS|UM|UMA|O|A|OF|THE))?\s+([A-ZÀ-Ú0-9]{2,20})(?:[!,\.]|\b)/g;
  let ctxMatch;
  while ((ctxMatch = soundContextRegex.exec(upper)) !== null) {
    const token = ctxMatch[1].trim();
    if (detectIsolatedOnomatopoeia(token) || SFX_SOUNDS.includes(token)) {
      found.add(token);
    }
  }

  return Array.from(found);
};

export const isStrictAllCaps = (str: string) => {
  const hasLowerCase = /[a-zà-öø-ÿ]/.test(str);
  const hasAlphanumeric = /[a-zA-ZÀ-ÖØ-öø-ÿ0-9]/.test(str);
  return !hasLowerCase && hasAlphanumeric;
};

/**
 * Detects if a line contains a ShotCue (camera direction).
 * Runs before character cue detection.
 */
export const detectShotCue = (line: string): CameraMeta | null => {
  const trimmed = line.trim();
  if (!trimmed) return null;

  const upperLine = normalizeText(trimmed);

  // 1. Pre-collect all dictionary entries for prefix matching
  const entries: { category: string, key: string, synonym: string, lang: "en" | "pt" | "es" | "fr" }[] = [];
  const langs: ("en" | "pt" | "es" | "fr")[] = ["en", "pt", "es", "fr"];

  for (const category in SHOT_CUE_DICTIONARY) {
    for (const key in SHOT_CUE_DICTIONARY[category]) {
      for (const lang of langs) {
        for (const synonym of SHOT_CUE_DICTIONARY[category][key][lang]) {
          entries.push({ category, key, synonym: normalizeText(synonym), lang });
        }
      }
    }
  }

  // Sort entries by synonym length descending to match longest first (e.g., "EXTREME LONG SHOT" before "LONG SHOT")
  entries.sort((a, b) => b.synonym.length - a.synonym.length);

  const result: CameraMeta = {};
  let foundSomething = false;

  // We want to find the LONGEST synonym that matches the START of the line
  // and is either the whole line or followed by space/punctuation.
  for (const entry of entries) {
    const syn = entry.synonym;
    if (upperLine.startsWith(syn)) {
      // Check if it's a "clean" match (whole word)
      const remainder = upperLine.slice(syn.length);
      const isWholeWord = remainder.length === 0 || /^[^A-Z0-9]/.test(remainder);

      if (isWholeWord) {
        if (!(result as any)[entry.category]) {
          (result as any)[entry.category] = entry.key;
          result.rawCue = trimmed.slice(0, trimmed.length - remainder.length).trim();
          result.langHint = entry.lang;
          foundSomething = true;
          // Note: we continue to pick up other categories if possible, but usually one line = one cue.
          // However, we must be careful not to overwrite a longer match with a shorter one in the same line.
          // Since we sorted by length, we are already picking the longest match for any category.
        }
      }
    }
  }

  if (!foundSomething) return null;

  return result;
};

/**
 * Formal Detector for Camera Directions.
 * Returns true if the line strictly contains camera cues.
 */
export const detectCameraDirection = (line: string): boolean => {
  return detectShotCue(line) !== null;
};

export type BlockClassification = 'empty' | 'metadata' | 'scene-heading' | 'transition' | 'parenthetical' | 'potential-character' | 'sfx' | 'camera-direction' | 'general';

export const classifyLineBase = (line: string): BlockClassification => {
  const trimmed = line.trim();
  if (!trimmed) return 'empty';

  // Cleaning: Remove markdown (*, _, #) and surrounding brackets/parens ([...], (...)) if they wrap the WHOLE line
  // This handles [EXT. LOCATION - DAY] style
  let cleanContent = trimmed.replace(/^[\*\_\#]+|[\*\_\#]+$/g, '').trim();
  if (/^\[.*\]$/.test(cleanContent)) {
    cleanContent = cleanContent.slice(1, -1).trim();
  } else if (/^\(.*\)$/.test(cleanContent) && !/^\(.*\)$/.test(trimmed)) {
    // Only unwrap parens if trimmed line wasn't parens (to preserve parentheticals)
    // Actually, parenthetical logic handles (text) later.
    // But if it's (EXT. LOCATION), it's a scene.
    // Let's rely on regex below.
  }

  const upper = cleanContent.toUpperCase();
  const cleanUpper = upper; // Re-assign for consistency with previous code structure

  // 0) Priority: Camera Direction (Strict Exclusion)
  if (detectCameraDirection(line)) return 'camera-direction';

  // 1) Hard-exclude for Time Markers and Transitions
  const isMarker = ALL_MARKERS.some(marker => cleanUpper === marker || cleanUpper.startsWith(marker + ":"));
  if (isMarker) {
    const isTypicTransition = [
      ...TRANSITION_MARKERS_PT, ...TRANSITION_MARKERS_EN,
      'CORTE PARA:', 'CUT TO:', 'DISSOLVE TO:', 'FADE IN', 'FADE OUT', 'MAIS TARDE:'
    ].some(m => cleanUpper === m || cleanUpper.startsWith(m.toUpperCase() + ":") || cleanUpper.startsWith(m.toUpperCase()));

    if (cleanUpper.endsWith(":") || isTypicTransition) return 'transition';
    return 'general'; // Action
  }

  const colonIndex = cleanUpper.indexOf(':');
  if (colonIndex !== -1) {
    const key = cleanUpper.substring(0, colonIndex).trim();
    const cleanKey = key.replace(/[\*\_\#]/g, '');
    if (METADATA_KEYWORDS.includes(cleanKey)) return 'metadata';
  }
  const isStandaloneMetadata = [
    'BY', 'WRITTEN BY', 'ESCRITO POR', 'UM FILME DE', 'DIREÇÃO', 'PRODUÇÃO', 'AUTHOR', 'TITLE', 'AUTOR', 'TÍTULO'
  ].some(k => cleanUpper === k || cleanUpper.startsWith(k + ':') || (cleanUpper.startsWith(k + ' ') && cleanUpper.length > k.length + 1));

  if (isStandaloneMetadata) return 'metadata';

  if (METADATA_KEYWORDS.some(k => cleanUpper === k || cleanUpper.includes(`${k} (`))) return 'metadata';

  // Updated Regex to support loose matching (brackets, numbers, etc.)
  // We check against 'cleanUpper' which has stripped brackets.
  const isScene = /^(?:\d+[A-Z]?[\.\)\-\s]*)?(?:(?:FLASHBACK|FLASHFORWARD|SONHO|PESADELO|DREAM|NIGHTMARE|SUEÑO|PESADILLA|RÊVE|CAUCHEMAR)\s+-\s+)?(?:INT\.|EXT\.|I\/E|INT |EXT |CENA\b|SCENE\b|EST\.|ESTAB\.)/.test(cleanUpper) ||
    /^(?:\[)?(?:\d+[A-Z]?[\.\)\-\s]*)?(?:INT\.|EXT\.|I\/E|INT |EXT |CENA\b|SCENE\b)/.test(cleanUpper); // fallback for uncleaned brackets

  if (isScene) return 'scene-heading';

  const transitionWhitelist = [
    'CORTE PARA:', 'CUT TO:', 'DISSOLVE TO:', 'FADE IN', 'FADE OUT.', 'FADE TO BLACK.',
    'MAIS TARDE:', 'LATER:', 'TIME CUT:', 'SMASH CUT TO:', 'JUMP CUT TO:', 'FADE IN:', 'FADE OUT.'
  ];
  if (transitionWhitelist.some(t => cleanUpper === t || cleanUpper.startsWith(t))) return 'transition';
  if (COMMON_TRANSITIONS.includes(cleanUpper)) return 'transition';

  if (trimmed.startsWith('(') && trimmed.endsWith(')')) return 'parenthetical';

  const sfxPrefixesEarly = ['SFX:', 'VFX:', 'FX:', 'EFEITO:', 'SOM:', 'SOUND:', 'SON:', 'MUSIQUE:', 'MUSICA:', 'SONIDO:', 'EFECTO:', 'RUIDO:', 'BRUIT:', '(SFX', '(FX'];
  if (sfxPrefixesEarly.some(p => cleanUpper.startsWith(p))) {
    return 'sfx';
  }

  const soundIndicators = ['SOM DE', 'SOUND OF', 'SFX', 'SONOROPLASTIA', 'BARULHO', 'EFEITO', 'SON', 'MUSIQUE', 'BRUIT', 'MUSICA', 'SONIDO', 'EFECTO', 'RUIDO'];
  const parenMatch = trimmed.match(/\(([^)]+)\)/);
  if (parenMatch) {
    const parenContent = parenMatch[1].toUpperCase();
    if (soundIndicators.some(ind => parenContent.includes(ind))) {
      if (isStrictAllCaps(trimmed.split('(')[0].trim()) || cleanUpper.startsWith('SFX')) {
        return 'sfx';
      }
    }
  }

  if (detectIsolatedOnomatopoeia(trimmed)) {
    return 'sfx';
  }

  if (isStrictAllCaps(trimmed)) {
    if (trimmed.length < 2 || trimmed.length > 50) return 'general';

    const sfxPrefixes = ['SFX:', 'VFX:', 'FX:', 'EFEITO:', 'SOM:', 'SOUND:', '(SFX', '(FX'];
    const soundPhraseStarts = ['UM SOM ', 'O SOM ', 'A SOUND ', 'THE SOUND ', 'SOM DE ', 'SOUND OF '];

    if (sfxPrefixes.some(p => cleanUpper.startsWith(p)) || soundPhraseStarts.some(p => cleanUpper.startsWith(p))) {
      return 'sfx';
    }

    const cleanForSfxMatch = cleanUpper.replace(/[!\.]+$/, '');
    const isKnownSfx = SFX_SOUNDS.some(sound => {
      return cleanForSfxMatch === sound || cleanForSfxMatch === sound + '!';
    });
    if (isKnownSfx) return 'sfx';

    if (detectIsolatedOnomatopoeia(trimmed)) {
      const hasRepeatedLetters = /(.)\1{2,}/.test(cleanUpper);
      const hasConsonantCluster = /[BCDFGHJKLMNPQRSTVWXYZ]{3,}/.test(cleanUpper);
      const endsWithExclamation = trimmed.endsWith('!');
      const isActuallyKnown = SFX_SOUNDS.includes(cleanForSfxMatch);

      if (isActuallyKnown || hasRepeatedLetters || hasConsonantCluster || endsWithExclamation) {
        return 'sfx';
      }
    }

    const sfxPatterns = ['SFX', 'FX', '(SFX)', '(FX)', 'RUIDO', 'ESTOURO'];
    const hasSFXKeyword = sfxPatterns.some(k => cleanUpper.includes(k));
    if (hasSFXKeyword) {
      return 'sfx';
    }

    if (/[:—–\[\]\{\}\/\\|=]/.test(trimmed)) return 'general';

    const firstWord = cleanUpper.split(/[\s\(\.]/)[0];
    if (CHARACTER_BLACKLIST.includes(firstWord)) return 'general';
    if (CHARACTER_BLACKLIST.includes(cleanUpper)) return 'general';

    const honorableMentions = ['MR.', 'MRS.', 'MS.', 'DR.', 'SR.', 'JR.', 'SGT.', 'LT.', 'CAPT.', 'PROF.', 'ENG.'];
    const endsWithPeriod = cleanUpper.replace(/\s*\(.*\)$/, '').endsWith('.');
    const isHonorific = honorableMentions.some(h => cleanUpper.startsWith(h));

    if (endsWithPeriod && !isHonorific) {
      if (detectIsolatedOnomatopoeia(trimmed.replace(/\.$/, ''))) return 'sfx';
      return 'general';
    }

    if (/^[A-ZÀ-Ü0-9][A-ZÀ-Ü0-9\s\.\''\-\#\_]*(?:\s*\(.*\))?$/.test(cleanUpper)) {
      return 'potential-character';
    }
  }

  return 'general';
};

export const parseTextToBlocks = (text: string): ScriptBlock[] => {
  const sanitizedText = runMechanicalLinter(text);
  const rawLines = sanitizedText.split(/\r?\n/);
  const blocks: ScriptBlock[] = [];
  let inScriptBody = false;
  let isCurrentAiSuggestion = false;

  // PASS 0: Extract markers and clean lines
  const lines: string[] = [];
  const lineSuggestionFlags: boolean[] = [];

  for (const rawLine of rawLines) {
    const trimmed = rawLine.trim();
    if (trimmed === '[[AI_SUGG_START]]') {
      isCurrentAiSuggestion = true;
      continue;
    }
    if (trimmed === '[[AI_SUGG_END]]') {
      isCurrentAiSuggestion = false;
      continue;
    }
    lines.push(rawLine);
    lineSuggestionFlags.push(isCurrentAiSuggestion);
  }

  // PASS 1: Pre-scan for Confirmed Characters
  const confirmedCharacters = new Set<string>();
  for (const line of lines) {
    const type = classifyLineBase(line);
    if (type === 'potential-character') {
      const clean = line.trim().replace(/^[\*\_\#]+|[\*\_\#]+$/g, '').replace(/\s*\(.*\)/, '').replace(/[:\-\.]+$/, '').toUpperCase();

      if (COMMON_INANIMATE_OBJECTS.includes(clean)) continue;

      const firstWord = clean.split(/\s+/)[0];
      if (COMMON_INANIMATE_OBJECTS.includes(firstWord)) continue;

      if (CHARACTER_BLACKLIST.includes(clean)) continue;

      // Article-prefixed lines are likely descriptive sub-headings, not character names
      // e.g. "THE EYE", "A MOMENT", "O OLHO", "THE ROOM"
      if (ARTICLE_PREFIXES.includes(firstWord) && clean.split(/\s+/).length >= 2) continue;

      const words = clean.split(/\s+/).length;
      if (words <= 4) {
        confirmedCharacters.add(clean);
      }
    }
  }

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const classification = classifyLineBase(line);
    const cleanContent = line.trim().replace(/^[\*\_\#]+|[\*\_\#]+$/g, '');

    const lineSuggestion = lineSuggestionFlags[i];

    if (classification === 'empty') {
      blocks.push({ id: `block-${Date.now()}-${i}`, type: 'action', content: '', isAiSuggestion: lineSuggestion });
      continue;
    }

    if (classification === 'metadata') {
      blocks.push({ id: `block-${Date.now()}-${i}`, type: 'action', content: cleanContent, metadata: true, isAiSuggestion: lineSuggestion } as any);
      continue;
    }

    if (classification === 'scene-heading') {
      inScriptBody = true; // Context Lock: Script body starts here
      const contentWithoutNumber = cleanContent.replace(/^\d+[A-Z]?[\.\)\-\s]+\s*/, '');
      const finalContent = contentWithoutNumber.trim() || cleanContent;
      blocks.push({ id: `block-${Date.now()}-${i}`, type: 'scene-heading', content: finalContent.toUpperCase(), isAiSuggestion: lineSuggestion });
      continue;
    }

    if (classification === 'transition') {
      // Context Lock: Transitions only allowed in script body
      if (inScriptBody) {
        blocks.push({ id: `block-${Date.now()}-${i}`, type: 'transition', content: cleanContent.toUpperCase(), isAiSuggestion: lineSuggestion });
      } else {
        const normalized = normalizeActionCaps(cleanContent, confirmedCharacters);
        blocks.push({ id: `block-${Date.now()}-${i}`, type: 'action', content: normalized, isAiSuggestion: lineSuggestion });
      }
      continue;
    }

    if (classification === 'camera-direction') {
      const cameraMeta = detectShotCue(line);
      // Lookahead for intent text (non-caps, next non-empty line)
      let intentText = "";
      let lookaheadIdx = i + 1;
      while (lookaheadIdx < lines.length) {
        const nextLine = lines[lookaheadIdx].trim();
        if (!nextLine) { lookaheadIdx++; continue; }
        const nextClass = classifyLineBase(nextLine);
        if (nextClass === 'general') {
          intentText = nextLine;
          // Consume intent line
          i = lookaheadIdx;
        }
        break;
      }

      blocks.push({
        id: `block-${Date.now()}-${i}`,
        type: 'camera-direction',
        content: intentText ? `${cleanContent}\n${intentText}` : cleanContent,
        camera: cameraMeta || undefined,
        isAiSuggestion: lineSuggestion
      });
      continue;
    }

    if (classification === 'sfx') {
      const prevBlock = blocks.slice().reverse().find(b => b.content !== '');
      if (prevBlock && (prevBlock.type === 'character' || prevBlock.type === 'parenthetical') && !cleanContent.toUpperCase().startsWith('SFX:')) {
        blocks.push({ id: `block-${Date.now()}-${i}`, type: 'dialogue', content: cleanContent, isAiSuggestion: lineSuggestion });
      } else {
        blocks.push({ id: `block-${Date.now()}-${i}`, type: 'sfx', content: cleanContent.toUpperCase(), isAiSuggestion: lineSuggestion });
      }
      continue;
    }

    if (classification === 'potential-character') {
      // Extra heuristics: ALL CAPS and <= 4 words
      const words = cleanContent.split(/\s+/).length;
      if (words > 4) {
        const normalized = normalizeActionCaps(cleanContent, confirmedCharacters);
        blocks.push({ id: `block-${Date.now()}-${i}`, type: 'action', content: normalized });
        continue;
      }

      let isValidCharacter = false;
      let lookaheadIndex = i + 1;
      let searchDepth = 0;
      const MAX_DEPTH = 3;
      let foundParenthetical = false;

      while (lookaheadIndex < lines.length && searchDepth < MAX_DEPTH) {
        const nextLine = lines[lookaheadIndex];
        const nextClass = classifyLineBase(nextLine);

        if (nextClass === 'empty') {
          lookaheadIndex++;
          continue;
        }

        // Parenthetical is considered part of the character/dialogue flow
        if (nextClass === 'parenthetical') {
          foundParenthetical = true;
          lookaheadIndex++;
          continue;
        }

        if (nextClass === 'general' || (nextClass === 'sfx' && !nextLine.trim().toUpperCase().startsWith('SFX:'))) {
          isValidCharacter = true;
          break;
        }

        if (nextClass === 'scene-heading' || nextClass === 'transition' || nextClass === 'metadata' || nextClass === 'potential-character' || nextClass === 'camera-direction') {
          isValidCharacter = false;
          break;
        }

        searchDepth++;
        lookaheadIndex++;
      }

      // ── DESCRIPTIVE HEADER DETECTION ──
      // After the lookahead confirms a potential character, apply content analysis
      // to catch descriptive sub-headings like "THE EYE", "A MOMENT", "THE ROOM".
      if (isValidCharacter && !foundParenthetical) {
        const cleanUpper = cleanContent.toUpperCase().replace(/\s*\(.*\)$/, '').trim();
        const firstWord = cleanUpper.split(/\s+/)[0];
        const hasArticlePrefix = ARTICLE_PREFIXES.includes(firstWord) && cleanUpper.split(/\s+/).length >= 2;

        if (hasArticlePrefix) {
          // Gather the following text block to analyze its nature
          let followingText = '';
          let collectIdx = lookaheadIndex;
          let collectedLines = 0;
          while (collectIdx < lines.length && collectedLines < 8) {
            const fl = lines[collectIdx].trim();
            if (!fl) break; // stop at blank line
            const flClass = classifyLineBase(fl);
            if (flClass === 'scene-heading' || flClass === 'potential-character' || flClass === 'transition') break;
            followingText += ' ' + fl;
            collectIdx++;
            collectedLines++;
          }
          followingText = followingText.trim();

          // Signal 1: Multi-sentence text (3+ sentences) → likely description, not dialogue
          const sentenceCount = (followingText.match(/[.!?]+\s/g) || []).length + (followingText.match(/[.!?]+$/g) ? 1 : 0);

          // Signal 2: Third-person narrative markers → description, not dialogue
          const narrativeMarkers = /\b(It's|It is|Its|There's|There is|There are|The |He |She |They |His |Her |Their |We see|On the|In the|Ele |Ela |Eles |Elas |Há |Existe|É um|É uma|São |Na |No |Nos |Uma |Um )\b/i;
          const hasNarrativeMarkers = narrativeMarkers.test(followingText);

          // Signal 3: Long continuous text (>120 chars without line breaks) → action paragraph
          const isLongParagraph = followingText.length > 120;

          // Decision: if 2+ signals indicate description, treat as action
          const descriptionSignals = [sentenceCount >= 3, hasNarrativeMarkers, isLongParagraph].filter(Boolean).length;

          if (descriptionSignals >= 2) {
            // This is a descriptive sub-heading, not a character name
            blocks.push({ id: `block-${Date.now()}-${i}`, type: 'action', content: cleanContent, isAiSuggestion: lineSuggestion });
            continue;
          }
        }
      }

      if (isValidCharacter) {
        let finalName = cleanContent;
        const HONORIFICS_LIST = ['MR.', 'MRS.', 'MS.', 'DR.', 'SR.', 'JR.', 'SGT.', 'LT.', 'CAPT.'];
        if (finalName.endsWith('.') && !HONORIFICS_LIST.some(h => finalName.includes(h))) {
          finalName = finalName.slice(0, -1);
        }

        blocks.push({ id: `block-${Date.now()}-${i}`, type: 'character', content: finalName, isAiSuggestion: lineSuggestion });
        continue;
      } else {
        const normalized = normalizeActionCaps(cleanContent, confirmedCharacters);
        blocks.push({ id: `block-${Date.now()}-${i}`, type: 'action', content: normalized, isAiSuggestion: lineSuggestion });
        continue;
      }
    }

    if (classification === 'parenthetical') {
      blocks.push({ id: `block-${Date.now()}-${i}`, type: 'parenthetical', content: cleanContent, isAiSuggestion: lineSuggestion });
      continue;
    }

    // Default Action Fallback
    const normalized = normalizeActionCaps(cleanContent, confirmedCharacters);
    blocks.push({ id: `block-${Date.now()}-${i}`, type: 'action', content: normalized, isAiSuggestion: lineSuggestion });
  }

  // PASS 2: Propagate Dialogue Type
  for (let i = 1; i < blocks.length; i++) {
    const current = blocks[i];
    // We only convert 'action' or 'sfx' to 'dialogue' if they seem to continue a speech
    if ((current.type === 'action' || current.type === 'sfx') && current.content !== '') {
      let prevType: BlockType | null = null;
      let hasGap = false;

      // Look back for the nearest non-empty block
      for (let j = i - 1; j >= 0; j--) {
        if (blocks[j].content === '') {
          hasGap = true;
          // Continue searching for the type of the element that preceded the gap
        } else {
          prevType = blocks[j].type;
          break;
        }
      }

      /**
       * REPAIR PROTOCOL: Dialogue Propagation Rules
       * 1. Character/Parenthetical -> induces Dialogue in next block ONLY if no gap.
       * 2. Dialogue -> induces Dialogue in next block IF there is no gap (blank line).
       * 3. (Optional) Dialogue -> induces Dialogue across ONE gap if current block starts with lowercase.
       */
      const isLowercaseStart = /^[a-zà-öø-ÿ]/.test(current.content);
      const shouldBeDialogue =
        ((prevType === 'character' || prevType === 'parenthetical') && (!hasGap || isLowercaseStart)) ||
        (!hasGap && prevType === 'dialogue') ||
        (hasGap && prevType === 'dialogue' && isLowercaseStart);

      if (shouldBeDialogue) {
        // Only convert SFX to dialogue if it doesn't have an explicit marker
        if (current.type === 'sfx' && current.content.toUpperCase().startsWith('SFX:')) {
          // Keep as SFX
        } else {
          current.type = 'dialogue';
        }
      }
    }
  }

  // PASS 3: Merge adjacent elements of the same type (mainly for Dialogue)
  const finalBlocks: ScriptBlock[] = [];
  for (let i = 0; i < blocks.length; i++) {
    const block = blocks[i];
    let prevInFinal = finalBlocks[finalBlocks.length - 1];

    // Check if there was a gap (empty block) in the original list
    const hasGapBefore = i > 0 && blocks[i - 1].content === '';

    const isContinuation = block.type === 'dialogue' && /^[a-zà-öø-ÿ]/.test(block.content);

    // If we are looking at a potential continuation but the previous block is empty,
    // we might need to "bridge" the gap by removing the empty block.
    if (isContinuation && hasGapBefore && prevInFinal && prevInFinal.content === '') {
      // Look back one more step
      const candidate = finalBlocks[finalBlocks.length - 2];
      if (candidate && candidate.type === 'dialogue') {
        finalBlocks.pop(); // Remove the gap
        prevInFinal = candidate; // Set prevInFinal to the dialogue block
      }
    }

    // Since we potentially updated prevInFinal, check merging again
    // We allow merging if !hasGapBefore OR if it is a valid Continuation
    if (prevInFinal && prevInFinal.type === 'dialogue' && block.type === 'dialogue' && (!hasGapBefore || isContinuation)) {
      // Merge into the existing dialogue block
      prevInFinal.content = `${prevInFinal.content} ${block.content}`.trim();
      // If any of the merged blocks was an AI suggestion, the result is an AI suggestion
      if (block.isAiSuggestion) {
        prevInFinal.isAiSuggestion = true;
      }
    } else {
      // Push as a new block (including empty ones)
      finalBlocks.push({ ...block });
    }
  }

  if (finalBlocks.length === 0) {
    finalBlocks.push({ id: `init-${Date.now()}-1`, type: 'scene-heading', content: 'INT. START HERE - DAY' });
    finalBlocks.push({ id: `init-${Date.now()}-2`, type: 'action', content: '' });
  }
  return finalBlocks;
};

export const createTitlePageBlocks = (
  title: string,
  author: string,
  byLine: string = 'Escrito por',
  basedOn?: string,
  contactInfo?: string,
  basedOnLabelArg?: string
): ScriptBlock[] => {
  const ts = Date.now();
  const blocks: ScriptBlock[] = [];
  let blockIdCounter = 1;

  const addEmptyLines = (count: number) => {
    for (let i = 0; i < count; i++) {
      blocks.push({ id: `t-${ts}-${blockIdCounter++}`, type: 'action', content: '', metadata: true });
    }
  };

  // 1. Pula 16 linhas
  addEmptyLines(16);

  // 2. SCRIPT TITLE (Plain content, styling applied by renderer)
  blocks.push({ id: `t-${ts}-${blockIdCounter++}`, type: 'action', content: title.toUpperCase(), metadata: true, align: 'center' });

  // 3. Pula 3 linhas
  addEmptyLines(3);

  // 4. Written by Author Name (Same line as per Image 3)
  blocks.push({ id: `t-${ts}-${blockIdCounter++}`, type: 'action', content: `${byLine} ${author}`, metadata: true, align: 'center' });

  // 5. Pula 4 linhas
  addEmptyLines(4);

  // 6. Based on (If Any)
  if (basedOn && basedOn.trim()) {
    let basedOnLabel = basedOnLabelArg;

    if (!basedOnLabel) {
      basedOnLabel = byLine.toLowerCase().includes('por') || byLine.toLowerCase().includes('escrito') || byLine === 'Escrito por'
        ? 'Baseado em,'
        : 'Based on,';
    }

    blocks.push({ id: `t-${ts}-${blockIdCounter++}`, type: 'action', content: `${basedOnLabel} ${basedOn}`, metadata: true, align: 'center' });
  }

  // 7. Pula 22 linhas
  addEmptyLines(22);

  // 8. Contact Info (Bottom left placement indicated by spacing)
  if (contactInfo && contactInfo.trim()) {
    let text = contactInfo.trim();

    // Semantic split: Detect email followed by space and then a phone number (starting with + or digit)
    // This handles users who just type both in the same field without a separator
    const emailPhoneRegex = /([^\s@]+@[^\s@]+\.[^\s@]+)\s+([\+\d].*)/;
    const match = text.match(emailPhoneRegex);
    if (match) {
      text = `${match[1]}\n${match[2]}`;
    }

    // Split by common separators (slash, pipe, or newline)
    const lines = text.split(/[\/\n|]+/).map(s => s.trim()).filter(Boolean);
    lines.forEach(line => {
      blocks.push({ id: `t-${ts}-${blockIdCounter++}`, type: 'action', content: line, metadata: true, align: 'left' });
    });
  }

  // 9. Pula 08 linhas
  addEmptyLines(8);

  return blocks;
};

/**
 * Normalizes ALL CAPS phrases in Action blocks to Title Case/Sentence Case
 * to prevent them from being mistaken for Characters.
 * 
 * Rules:
 * - Only affects ALL CAPS phrases of 2+ words, or specific single words (COMMON_INANIMATE_OBJECTS).
 * - Ignores confirmed characters (whitelist).
 * - Ignores SFX, Camera keywords, and Transitions.
 * 
 * Example: "Ela pega uma PEDRA DE CASCALHO." -> "Ela pega uma Pedra de cascalho."
 */
export const normalizeActionCaps = (text: string, characterNamesConfirmed: Set<string> = new Set()): string => {
  // Regex to find potential CAPS phrases (2+ words or single words)
  // We look for [A-ZÀ-Ú]+ followed by (space [A-ZÀ-Ú]+)*
  // Min length 2 chars

  // Strategy: Split by bounds and process tokens? Or replace regex?
  // Let's use replacement function to have context.

  // Matches sequence of CAPS words. 
  // e.g. "PEDRA DE CASCALHO", "TALO RIJO", "CLANG"
  // We use word boundary to avoid matching inside typical words if possible, but ALL CAPS usually distinct.

  return text.replace(/\b[A-ZÀ-Ú0-9]{2,}(?:\s+[A-ZÀ-Ú0-9]{2,})*\b/g, (match) => {
    const clean = match.trim().toUpperCase();

    // 1. Check if it's a Confirmed Character -> KEEP CAPS
    if (characterNamesConfirmed.has(clean)) return match;

    // 2. Check if it's SFX -> KEEP CAPS
    if (isSfxToken(clean) || SFX_SOUNDS.includes(clean)) return match;

    // 3. Check for Transitions/Camera -> KEEP CAPS
    if (CAMERA_KEYWORDS.some(k => clean.includes(k))) return match;
    if (COMMON_TRANSITIONS.includes(clean + ':') || COMMON_TRANSITIONS.includes(clean + '.')) return match;

    // 4. Check for Group Character (Preserve for later extraction)
    // We pass the full text as context to check for agent verbs
    if (isGroupCharacter(clean, text)) return match;

    // 5. Identify if it should be normalized
    // If it's a single word, only normalize if it's a common inanimate object or NOT a potential character name (heuristic)
    // Actually, user wants to avoid Props in CAPS.
    // If it's single word and looks like a name (not in object list), maybe we keep it as potential character?
    // But user Rule 4: "Se candidato estiver próximo de verbo... tratar como prop".
    // For safety in this utility: If it's in COMMON_INANIMATE_OBJECTS -> Normalize.
    // If it's multi-word (2+) and NOT a character -> Normalize (Prop/VFX usually).

    const isSingleWord = !clean.includes(' ');

    if (isSingleWord) {
      if (COMMON_INANIMATE_OBJECTS.includes(clean)) {
        // Normalize "PEDRA" -> "Pedra"
        return clean.charAt(0) + clean.slice(1).toLowerCase();
      }
      // If single uppercase word and unknown, risky to change (could be new Character). Keep CAPS.
      return match;
    }

    // Multi-word case (e.g. "PEDRA DE CASCALHO")
    // Normalize to Sentence case: "Pedra de cascalho"
    const lower = clean.toLowerCase();
    return clean.charAt(0) + lower.slice(1);
  });
};

/**
 * Checks if a token represents a Group Character based on lexicon or plural agent heuristic.
 * Returns true if it should be classified as a CHARACTER (Group).
 */
export const isGroupCharacter = (token: string, contextLine: string): boolean => {
  const cleanToken = token.trim().toUpperCase();
  if (!cleanToken) return false;

  // 1. Lexicon Check
  const isInLexicon = GROUP_CHARACTER_LEXICON_PT.includes(cleanToken);

  // 2. Plural Heuristic (Ends in S/ES and length > 3)
  const isPlural = (cleanToken.endsWith('S') || cleanToken.endsWith('ES')) && cleanToken.length > 3;

  if (!isInLexicon && !isPlural) return false;

  // 3. Agent Verb Proximity Check
  // We look for agent verbs in the context line.
  // If found within ~5 words (simplified: just present in the line near the token?)
  // Rigorous: Find token index, find verb index, check distance.
  // Heuristic: If line contains BOTH token AND an agent verb, it's likely an agent.

  const upperContext = contextLine.toUpperCase();

  // Check if any agent verb is present in the line
  const hasAgentVerb = AGENT_VERBS_PT.some(verb => {
    // Simple includes check for now, can be regex for whole word
    const regex = new RegExp(`\\b${verb}\\b`);
    return regex.test(upperContext);
  });

  return hasAgentVerb;
};

/**
 * Validates if the provided text adheres to standard script formatting.
 * Used to pre-validate imports and reject non-script documents (e.g., books, articles).
 */
export const isValidScriptFormat = (text: string): boolean => {
  if (!text || text.length < 50) return false; // Too short to be a valid script

  const upperText = text.toUpperCase();

  // 1. Check for Scene Heading Keywords
  // Must have at least one valid scene heading start
  const hasSceneHeading = SCENE_HEADINGS_START.some(marker => {
    // Check if marker exists as a distinct token or start of line
    const regex = new RegExp(`(?:^|\\n)\\s*${marker.replace('.', '\\.')}`, 'i');
    return regex.test(text);
  });

  // 2. Check for Transitions or Camera Cues
  const hasTransition = COMMON_TRANSITIONS.some(trans => upperText.includes(trans));
  const hasCamera = CAMERA_KEYWORDS.some(cam => upperText.includes(cam));

  // 3. Check for Character Cues (Centered-ish uppercase lines)
  // Heuristic: Look for lines that are short (unders 30 chars), all caps, and not scene headings
  // This is harder to test on raw text without layout info, but we can look for "CHARACTER" pattern
  // For now, we rely on having at least Scene Headings OR (Transitions AND Camera)

  // Relaxed rule: A script MUST have at least one scene heading OR be very rich in other cues.
  // But standard scripts always have scene headings.
  // However, a user might paste a snippet.
  // For IMPORT (PDF/TXT), we expect a full document, so it SHOULD have scene headings.

  if (hasSceneHeading) return true;

  // Fallback: If no scene heading (maybe a fragment?), checks for aggressive formatting
  // e.g. "FADE IN:" at start, or multiple "CUT TO:"
  if (hasTransition && (upperText.includes('DIALOGUE') || upperText.includes('PERSONAGEM'))) {
    // Very weak signal, essentially if it lacks scene headings it's suspicious.
    // Let's stick to: MUST have Scene Headings OR standard Transitions.
    return true;
  }

  if (hasTransition && hasCamera) return true;

  return false;
};

/**
 * Detects the script format based on textual markers.
 * Uses weighted scoring of format-specific markers to determine
 * the most likely script type: Documentary, Animation, Advertisement, or Film (default).
 *
 * Each format has its own set of exclusive/medium/low markers with weights.
 * The format with the highest score above its threshold (≥ 4) wins.
 * If no format reaches the threshold, defaults to 'Film'.
 *
 * DOCUMENTARY markers: [ENTREVISTA], [COBERTURA], (ON CAMERA), FOTO P&B, (V.O.)+(ON CAMERA)
 * ANIMATION markers: SEQUÊNCIA MUSICAL, VFX:, SFX:+VFX:, ESPAÇO ABSTRATO, voice-acting parentheticals
 * ADVERTISEMENT markers: PACKSHOT:, SUPER:, LOWER THIRD:, DISCLAIMER, VERSÃO A/B
 */
export const detectScriptFormat = (text: string): ScriptFormatType => {
  if (!text || text.length < 50) return 'Film';

  let docScore = 0;
  let animScore = 0;
  let adScore = 0;

  // ╔══════════════════════════════════════════════════════════════╗
  // ║                      DOCUMENTARY                           ║
  // ╚══════════════════════════════════════════════════════════════╝

  // [ENTREVISTA] / [INTERVIEW] — exclusive (+3)
  const entrevistaMatches = text.match(/\[ENTREVISTA\]|\[INTERVIEW\]/gi);
  if (entrevistaMatches) {
    docScore += Math.min(entrevistaMatches.length, 2) * 3;
  }

  // Depoimentos / Talking Heads — strong indicator (+3)
  // User definition: "Presença de Entrevistas (Talking Heads)... depoimentos diretos... personagens reais"
  const depoimentoMatches = text.match(/DEPOIMENTO|TESTIMONIAL|ENTREVISTAD[OA]|INTERVIEWEE|TALKING HEAD|CABEÇA FALANTE|SONORA/gi);
  if (depoimentoMatches) {
    docScore += Math.min(depoimentoMatches.length, 3) * 3;
  }

  // [COBERTURA] / [IMAGEM DE COBERTURA] / [COVERAGE] / [B-ROLL] — exclusive (+3)
  const coberturaMatches = text.match(/\[(?:IMAGEM DE )?COBERTURA\]|\[COVERAGE\]|\[B-ROLL\]/gi);
  if (coberturaMatches) {
    docScore += Math.min(coberturaMatches.length, 2) * 3;
  }

  // (ON CAMERA) — interview setup (+2)
  const onCameraMatches = text.match(/\(ON CAMERA\)/gi);
  if (onCameraMatches) {
    docScore += Math.min(onCameraMatches.length, 3) * 2;
  }

  // Archival material: Expanded keywords (+2)
  // User definition: "Uso de Cenas de Arquivo... reconstrução histórica"
  const archivalMatches = text.match(/FOTO P&B|FOTO P\sE\sB|IMAGEM DE ARQUIVO|STOCK FOOTAGE|FOOTAGE DE ARQUIVO|MATERIAL DE ARQUIVO|CENA DE ARQUIVO|VIDEO DE ARQUIVO|ARQUIVO PESSOAL|FOTOS? DE ARQUIVO|ARCHIVE FOOTAGE|ARCHIVAL/gi);
  if (archivalMatches) {
    docScore += Math.min(archivalMatches.length, 2) * 2;
  }

  // (V.O.) co-occurring with (ON CAMERA) — A-ROLL/B-ROLL pattern (+2)
  const hasVO = /\(V\.O\.\)/i.test(text);
  const hasOff = /\(OFF\)|EM OFF/i.test(text); // Common in PT docs
  const hasOnCamera = /\(ON CAMERA\)/i.test(text);

  if ((hasVO || hasOff) && hasOnCamera) {
    docScore += 2;
  }

  // NARRADOR (V.O.) / NARRATOR (V.O.) — Institutional/Expository (+1)
  if (/NARRADOR\s*\(V\.O\.\)|NARRADOR\s*\(OFF\)|NARRATOR\s*\(V\.O\.\)/i.test(text)) {
    docScore += 1;
  }

  // Alphabetic montage A) B) C) on separate lines (+1)
  const montageLines = text.split(/\r?\n/).filter(l => /^\s*[A-C]\)\s+/.test(l));
  if (montageLines.length >= 3) {
    docScore += 1;
  }

  // ╔══════════════════════════════════════════════════════════════╗
  // ║                       ANIMATION                            ║
  // ╚══════════════════════════════════════════════════════════════╝

  // SEQUÊNCIA MUSICAL: / MUSICAL SEQUENCE: — exclusive (+4)
  if (/SEQU[ÊE]NCIA MUSICAL:|MUSICAL SEQUENCE:/i.test(text)) {
    animScore += 4;
  }

  // FIM DA SEQUÊNCIA MUSICAL / END OF MUSICAL SEQUENCE (+2)
  if (/FIM DA SEQU[ÊE]NCIA MUSICAL|END OF MUSICAL SEQUENCE/i.test(text)) {
    animScore += 2;
  }

  // VFX: blocks — strong animation signal (+3)
  const vfxMatches = text.match(/^VFX:/gim);
  if (vfxMatches) {
    animScore += Math.min(vfxMatches.length, 2) * 3;
  }

  // SFX: + VFX: co-occurring (+2)
  const hasSFX = /^SFX:/im.test(text);
  const hasVFX = /^VFX:/im.test(text);
  if (hasSFX && hasVFX) {
    animScore += 2;
  }

  // Fantasy/abstract sluglines (+2 each, cap 2)
  const fantasySluglines = text.match(/(?:MENTE DE|EST[ÔO]MAGO D[OE]|INTERIOR DO VULC[ÃA]O|NUVEM GIGANTE|ESPA[ÇC]O ABSTRATO|INSIDE .+(?:'S|THE) (?:MIND|STOMACH|BRAIN))/gi);
  if (fantasySluglines) {
    animScore += Math.min(fantasySluglines.length, 2) * 2;
  }

  // Voice-acting parentheticals: (falsete), (robótico), (tom grave), (voz de velho) (+1 each, cap 3)
  const voiceActingMatches = text.match(/\((?:falsete|rob[óo]tico|tom grave|voz de (?:velho|crian[çc]a|mulher)|falsetto|robotic|deep voice)\)/gi);
  if (voiceActingMatches) {
    animScore += Math.min(voiceActingMatches.length, 3);
  }

  // ╔══════════════════════════════════════════════════════════════╗
  // ║                     ADVERTISEMENT                          ║
  // ╚══════════════════════════════════════════════════════════════╝

  // PACKSHOT: — exclusive, mandatory in commercials (+5)
  if (/^PACKSHOT:/im.test(text)) {
    adScore += 5;
  }

  // SUPER: — fundamental in commercials (+2 each, cap 3)
  const superMatches = text.match(/^SUPER:/gim);
  if (superMatches) {
    adScore += Math.min(superMatches.length, 3) * 2;
  }

  // LOWER THIRD: — exclusive to commercials (+3)
  if (/^LOWER THIRD:/im.test(text)) {
    adScore += 3;
  }

  // DISCLAIMER — exclusive to commercials (+2)
  if (/^DISCLAIMER/im.test(text)) {
    adScore += 2;
  }

  // VERSÃO A / VERSÃO B / VERSION A / VERSION B — A/B testing (+2)
  if (/VERS[ÃA]O [A-C]|VERSION [A-C]/i.test(text)) {
    adScore += 2;
  }

  // TAGLINE: or explicit tagline pattern (+1)
  if (/TAGLINE:|tagline:/i.test(text)) {
    adScore += 1;
  }

  // ╔══════════════════════════════════════════════════════════════╗
  // ║                     RESOLUTION                             ║
  // ╚══════════════════════════════════════════════════════════════╝

  const THRESHOLD = 4;

  // Pick the format with the highest score, if it meets the threshold
  const scores: [ScriptFormatType, number][] = [
    ['Documentary', docScore],
    ['Animation', animScore],
    ['Advertisement', adScore],
  ];

  // Sort descending by score
  scores.sort((a, b) => b[1] - a[1]);

  const [bestFormat, bestScore] = scores[0];
  if (bestScore >= THRESHOLD) return bestFormat;

  return 'Film';
};
